import React, { DragEvent, useEffect, useRef, useState } from "react";
import "./index.less";
import * as SpecialEffectIcon from "../../SvgIconCom/SpecialEffectIcon";
import { v4 as uuidv4 } from "uuid";
import { eventBus } from "@obsbot/remo-utils";
import { throttle } from "lodash-es";
import { SpecialEffectTo, useGlobalStore } from "@obsbot/remo-stores";
import { SpecialEffectPreviewClassMap } from "../../../utils/LottieAnimationHandler";
import { Tour, TourProps } from "antd";
import { Trans, useTranslate } from "@obsbot/remo-i18n";
import SpecialEffectGuideIconSVG from "../specialEffectGuideIcon.svg";
import { useClickAway } from "react-use";

export type SpecialIconType = keyof typeof SpecialEffectIcon;
export type SpecialEffectIconUsed =
    | "FaceChangeIcon"
    | "TornadoIcon"
    | "WelcomeIcon"
    | "CryIcon"
    | "EmptyIcon"
    | "HeartIcon"
    | "BombIcon"
    | "SkateIcon"
    | "CelebrateIcon";
export interface OnSelectedIconProps {
    icon: SpecialIconType;
    toType: SpecialEffectTo;
    data?: string;
    toName?: string;
    actionID: string;
    soundEffect?: string;
}
export interface SpecialEffectProps {
    showKeys: SpecialIconType[];
    showOperateIcon?: boolean;
    onSelectedIcon?: (data: OnSelectedIconProps) => void;
}

export const SpecialEffect: React.FC<SpecialEffectProps> = ({
    showKeys = [],
    showOperateIcon = false,
    onSelectedIcon,
}) => {
    const t = useTranslate();
    // 是否进行新手引导
    const { needShowGuide, updateNeedShowGuide } = useGlobalStore();
    // 添加展开/收起状态
    const [isExpanded, setIsExpanded] = useState<boolean>(
        !needShowGuide.channel && needShowGuide.specialEffect ? true : false,
    );
    // 当前拖拽的元素
    const [draggedKey, setDraggedKey] = useState<SpecialIconType | null>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    // 当前拖拽到的用户ID
    const draggedUID = useRef("");
    // 新手引导的元素
    const guideRef = useRef<HTMLDivElement | null>(null);
    const [canShowTour, setCanShowTour] = useState(false);
    // 新手引导
    const guideStep: TourProps["steps"] = [
        {
            title: (
                <div className="guide-top">
                    <div className="guide-picture">
                        <img className="guide-image" src={SpecialEffectGuideIconSVG} />
                    </div>
                    <div className="guide-title">{t("special-effect-action")}</div>
                </div>
            ),
            placement: "left",
            arrow: true,
            description: (
                <Trans
                    components={{
                        action: <span className="channel-guide-special" />,
                    }}
                    i18nKey="special-effect-action-description"
                />
            ),
            target: guideRef.current,
        },
    ];
    // 点击区域的ref
    const allowClickRef = useRef<HTMLDivElement>(null);
    const clickWhitelist = [
        "ant-tour-description",
        "guide-title",
        "channel-guide-special",
        "guide-image",
    ];
    useClickAway(allowClickRef, e => {
        const el = e.target as HTMLElement;
        if (!clickWhitelist.some(allowed => el.classList.contains(allowed))) {
            updateNeedShowGuide({
                channel: false,
                specialEffect: false,
                isToJoined: false,
            });
        }
    });

    // 处理SmileIcon点击事件
    const handleSmileIconClick = (): void => {
        setIsExpanded(!isExpanded);
    };

    const handleDoubleClick = (icon: SpecialIconType, toType: SpecialEffectTo): void => {
        console.log("handleDoubleClick", icon, toType);
        onSelectedIcon &&
            onSelectedIcon({
                icon: icon,
                toType: SpecialEffectTo.All,
                actionID: uuidv4(),
            });
    };
    useEffect(() => {
        window.addEventListener("dragend", (): void => {
            draggedUID.current = "";
        });
        return () => {
            window.removeEventListener("dragend", (): void => {
                draggedUID.current = "";
            });
        };
    }, []);
    // 处理DragEnd事件
    const handleDragEnd = (e: DragEvent<HTMLDivElement>): void => {
        // e.preventDefault();
        setIsDragging(false);
        let target = document.elementFromPoint(e.clientX, e.clientY);
        if (target?.localName === "canvas") {
            target = target.parentElement;
        }
        const attr_uid = target?.getAttribute("data-uid");
        if (target && draggedKey && attr_uid) {
            eventBus.emit("drag-special-effect", {
                isStarted: false,
                to: attr_uid,
            });
            draggedUID.current = "";
            onSelectedIcon &&
                onSelectedIcon({
                    icon: draggedKey,
                    toType: SpecialEffectTo.User,
                    data: attr_uid,
                    actionID: uuidv4(),
                });
        }
        document.body.style.cursor = "default";
    };
    // 处理drag中途
    const handleOnDrag = throttle(
        (e: React.DragEvent<HTMLDivElement>) => {
            // e.preventDefault();
            e.dataTransfer.effectAllowed = "copy";
            e.dataTransfer.dropEffect = "copy";
            let target = document.elementFromPoint(e.clientX, e.clientY);
            if (target?.classList.contains("video-window-item")) {
                target = target.children[0];
            } else if (target?.localName === "canvas") {
                target = target.parentElement;
            }
            const attr_uid = target?.getAttribute("data-uid");
            if (target && attr_uid && draggedUID.current !== attr_uid && isDragging) {
                eventBus.emit("drag-special-effect", {
                    isStarted: true,
                    to: attr_uid,
                });
                draggedUID.current = attr_uid;
            } else if (!target || !attr_uid) {
                eventBus.emit("drag-special-effect", {
                    isStarted: false,
                    to: "",
                });
                draggedUID.current = "";
            }
        },
        100,
        { trailing: false },
    );

    useEffect(() => {
        if (!needShowGuide.channel && needShowGuide.specialEffect) {
            setIsExpanded(true);
        }
    }, [needShowGuide]);

    const handlePopupAlign = (popupDomNode: HTMLElement): void => {
        popupDomNode.style.left = "160px";
        popupDomNode.style.top = "unset";
        popupDomNode.style.bottom = "50px";
    };
    return (
        <div
            ref={guideRef}
            className={`special-effect-container ${isExpanded ? "expanded" : "collapsed"} ${!needShowGuide.channel && needShowGuide.specialEffect ? "special-effect-guide" : ""}`}
            onTransitionEnd={() => {
                setCanShowTour(true);
                setTimeout(() => {
                    const tour = document.querySelector(
                        ".ant-tour.custom-tour-guide.ant-tour-placement-left",
                    ) as HTMLDivElement | null;
                    if (tour) {
                        tour.style.left = "160px";
                        tour.style.top = "unset";
                        tour.style.bottom = "50px";
                    }
                }, 300);
            }}
        >
            {showOperateIcon && (
                <div className="control-icon" onClick={handleSmileIconClick}>
                    <img src={SpecialEffectIcon.SmileIconPNG} />
                    {!isExpanded && (
                        <img className="control-arrow" src={SpecialEffectIcon.ArrowIconPNG} />
                    )}
                </div>
            )}
            {isExpanded && (
                <div className="control-icon-hidden" onClick={handleSmileIconClick}>
                    <img className="control-arrow-close" src={SpecialEffectIcon.ArrowIconPNG} />
                </div>
            )}
            <div className="icons-scroll-container">
                {showKeys
                    .filter(e => e !== "SmileIconPNG")
                    .map((key, idx) => {
                        const IconComponent = SpecialEffectIcon[key as SpecialIconType];
                        return (
                            <div
                                key={key + idx}
                                className="icon-item"
                                draggable="true"
                                onDoubleClick={() => {
                                    handleDoubleClick(key, SpecialEffectTo.All);
                                }}
                                onDrag={e => {
                                    if (!isDragging) {
                                        return;
                                    }
                                    handleOnDrag(e);
                                    setDraggedKey(key);
                                }}
                                onDragEnd={handleDragEnd}
                                onDragStart={e => {
                                    // 确保在拖拽开始时设置鼠标样式
                                    e.dataTransfer.effectAllowed = "copy";
                                    document.body.style.cursor = "pointer";
                                    setIsDragging(true);
                                }}
                            >
                                {IconComponent && (
                                    <IconComponent
                                        className={
                                            SpecialEffectPreviewClassMap[
                                                key as SpecialEffectIconUsed
                                            ]
                                        }
                                    />
                                )}
                            </div>
                        );
                    })}
            </div>
            <Tour
                actionsRender={() => (
                    <div ref={allowClickRef} className="tour-guide-action-buttons">
                        <div
                            className="tour-guide-action-next"
                            onClick={() => {
                                updateNeedShowGuide({
                                    channel: false,
                                    specialEffect: false,
                                    isToJoined: false,
                                });
                            }}
                        >
                            <span>{t("copy-that")}</span>
                        </div>
                    </div>
                )}
                closable={false}
                gap={{ radius: 10, offset: 6 }}
                mask={false}
                open={
                    isExpanded &&
                    canShowTour &&
                    !needShowGuide.channel &&
                    needShowGuide.specialEffect
                }
                rootClassName="custom-tour-guide special-effect-guide"
                steps={guideStep}
                onPopupAlign={handlePopupAlign}
            />
        </div>
    );
};
